'use client'

import React, { useMemo } from 'react'

import CommonNavbar from '@/components/layouts/common/Navbar'

import { usePathname } from '@/hooks/common/usePathname'
import { useWindowWidth } from '@/hooks/common/useWindowWidth'
import { useActiveOrg } from '@/hooks/organization/useActiveOrg'

import { useRecipientSearchStore } from '@/stores/RecipientSearchStore'

import type { Breadcrumb } from '@/types/navigation/Navbar'

/**
 * Recipient search navbar component
 *
 * Displays breadcrumbs for navigation within the recipient search section.
 * Shows org > Recipients > [Parent Name] when viewing subsidiaries.
 */
const Navbar: React.FC = () => {
  const { activeOrg } = useActiveOrg()
  const { parentRecipient, parentRecipientName } = useRecipientSearchStore()

  const pathname = usePathname()
  const windowWidth = useWindowWidth()

  const breadcrumbs: Breadcrumb[] = useMemo(() => {
    const crumbs: Breadcrumb[] = [
      {
        name: 'Sweetspot',
        href: '/',
      },
    ]

    crumbs.push({
      name:
        pathname && pathname.includes('usaspending')
          ? 'Federal Recipients'
          : 'Recipients',
      href: '/recipients/search',
    })

    // Show parent company name when viewing subsidiaries
    if (parentRecipient && parentRecipientName) {
      crumbs.push({
        name: `${parentRecipientName} Subsidiaries`,
        href: `/recipients/search?parent=${parentRecipient}`,
      })
    }

    return crumbs
  }, [activeOrg, parentRecipient, parentRecipientName, pathname])

  // On mobile, show simplified breadcrumbs
  const mobileBreadcrumbs: Breadcrumb[] = useMemo(() => {
    if (parentRecipient && parentRecipientName) {
      return [
        { name: 'Recipients', href: '/recipients/search' },
        {
          name: parentRecipientName,
          href: `/recipients/search?parent=${parentRecipient}`,
        },
      ]
    }
    return [{ name: 'Recipients', href: '/recipients/search' }]
  }, [parentRecipient, parentRecipientName])

  return (
    <CommonNavbar
      isLoading={false}
      breadcrumbs={(windowWidth || 0) > 1023 ? breadcrumbs : mobileBreadcrumbs}
    />
  )
}

export default Navbar
